import { useCallback, useEffect, useState } from 'react';

import { fetchAllPages } from './fetchAllPages';
import { useTax1099Api } from './hooks/api/useTax1099ApiHook';
import {
  VendorListResult,
  VendorPage,
  invalidateVendors,
  loadVendorsOnce,
  vendorCacheKey,
} from './vendorCache';

/** The list endpoint caps page_size at 200 server-side. */
const PAGE_SIZE = 200;

interface Options {
  branchId: number | null;
  /** Year-End Review scopes to a chosen year; the other screens take the default. */
  taxYear?: number | null;
}

interface Result {
  vendors: any[];
  /** Page one's payload, which carries count and the branch-wide summary. */
  first: VendorPage;
  loading: boolean;
  /** Refetches past the cache -- for an explicit "Refresh Data" control. */
  reload: () => void;
}

/**
 * Every vendor on the branch, fetched once and shared.
 *
 * Each screen used to page through the whole list itself, so navigating
 * between them refetched the same rows. Two of them fetched only the first
 * page, which silently hid vendors past the 200th.
 */
export const useAllVendors = ({ branchId, taxYear }: Options): Result => {
  const { fetchVendorList } = useTax1099Api();
  const [state, setState] = useState<VendorListResult>({ rows: [], first: {} });
  const [loading, setLoading] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!branchId) {
      setState({ rows: [], first: {} });
      setLoading(false);
      return;
    }

    let live = true;
    setLoading(true);

    const getPage = (page: number) =>
      new Promise<VendorPage>((resolve, reject) => {
        const params: Record<string, any> = { branch_id: branchId, page, page_size: PAGE_SIZE };
        if (taxYear) {
          params.tax_year = taxYear;
        }
        fetchVendorList(
          params,
          (_msg: string, response: any) => resolve(response?.data?.data ?? {}),
          reject,
        );
      });

    loadVendorsOnce(vendorCacheKey(branchId, taxYear), () => fetchAllPages(getPage, PAGE_SIZE))
      .then((result) => {
        // The branch or year can change while a request is in flight; dropping
        // a late response beats rendering another branch's vendors.
        if (!live) return;
        setState(result);
        setLoading(false);
      })
      .catch(() => {
        if (!live) return;
        setState({ rows: [], first: {} });
        setLoading(false);
      });

    return () => {
      live = false;
    };
    // fetchVendorList has no referential stability -- the API hook returns a
    // fresh closure every render -- so including it would refetch forever.
    // eslint-disable-next-line
  }, [branchId, taxYear, reloadToken]);

  // Drop the cache first, or the effect below would re-read the same rows and
  // "Refresh Data" would do nothing.
  const reload = useCallback(() => {
    invalidateVendors(branchId);
    setReloadToken((n) => n + 1);
  }, [branchId]);

  return { vendors: state.rows, first: state.first, loading, reload };
};

export default useAllVendors;
